import { useState } from "react";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { toast } from "react-toastify";
import { DateOnly } from "components/common/Date";
import {
  approveLeaveApplication,
  cancelLeaveApplication,
  deleteLeaveApplication,
  rejectLeaveApplication,
} from "../services/LeaveApplicationService";
import { leaveApplicationEmployeeLabel } from "../utils/leaveApplicationDisplay";
import type { LeaveApplication } from "../types/leave-application.types";

export type LeaveApplicationAction = "approve" | "reject" | "delete" | "cancel";

interface Props {
  record: LeaveApplication | null;
  action: LeaveApplicationAction | null;
  onClose: () => void;
  onSuccess: (action: LeaveApplicationAction) => void;
}

function extractMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ?? fallback
  );
}

export function LeaveApplicationActionModals({
  record,
  action,
  onClose,
  onSuccess,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  const toggle = () => {
    if (!busy) {
      setRejectReason("");
      setCancelReason("");
      onClose();
    }
  };

  const displayTitle = record ? leaveApplicationEmployeeLabel(record) : "";

  const runApprove = async () => {
    if (!record) return;
    setBusy(true);
    try {
      const res = await approveLeaveApplication(record.id);
      if (res.success) {
        toast.success(res.message ?? "Approved.");
        onSuccess("approve");
      } else {
        toast.error(res.message ?? "Approve failed.");
      }
    } catch (err) {
      toast.error(extractMessage(err, "Approve failed."));
    } finally {
      setBusy(false);
    }
  };

  const runReject = async () => {
    if (!record) return;
    setBusy(true);
    try {
      const res = await rejectLeaveApplication(record.id, {
        reason: rejectReason.trim() || null,
      });
      if (res.success) {
        toast.success(res.message ?? "Rejected.");
        onSuccess("reject");
      } else {
        toast.error(res.message ?? "Reject failed.");
      }
    } catch (err) {
      toast.error(extractMessage(err, "Reject failed."));
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (!record) return;
    setBusy(true);
    try {
      const res = await deleteLeaveApplication(record.id);
      if (res.success) {
        toast.success(res.message ?? "Deleted.");
        onSuccess("delete");
      } else {
        toast.error(res.message ?? "Delete failed.");
      }
    } catch (err) {
      toast.error(extractMessage(err, "Delete failed."));
    } finally {
      setBusy(false);
    }
  };

  const runCancel = async () => {
    if (!record) return;
    setBusy(true);
    try {
      const res = await cancelLeaveApplication(record.id, {
        reason: cancelReason.trim() || null,
      });
      if (res.success) {
        toast.success(res.message ?? "Leave cancelled.");
        onSuccess("cancel");
      } else {
        toast.error(res.message ?? "Cancel failed.");
      }
    } catch (err) {
      toast.error(extractMessage(err, "Cancel failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal isOpen={action === "approve"} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Approve leave</ModalHeader>
        <ModalBody>
          Approve this application for <strong>{displayTitle}</strong>
          {record?.fromDate ? (
            <>
              {" "}
              from <DateOnly date={record.fromDate} /> to{" "}
              <DateOnly date={record.toDate} />
            </>
          ) : null}
          ? Approving will write <strong>LEAVE</strong> attendance for each day
          in range.
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" disabled={busy} onClick={toggle}>
            Close
          </Button>
          <Button
            color="success"
            type="button"
            disabled={busy}
            onClick={() => void runApprove()}
          >
            {busy ? "Working…" : "Approve"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={action === "reject"} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Reject leave</ModalHeader>
        <ModalBody>
          <Label className="form-label">Reason (optional)</Label>
          <Input
            type="textarea"
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason for rejection"
          />
          <p className="text-muted small mt-2 mb-0">
            The reason will be saved and visible on the application timeline.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" disabled={busy} onClick={toggle}>
            Close
          </Button>
          <Button
            color="danger"
            type="button"
            disabled={busy}
            onClick={() => void runReject()}
          >
            {busy ? "Working…" : "Reject"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={action === "delete"} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Delete leave application</ModalHeader>
        <ModalBody>
          <p className="mb-2">
            Delete this pending request for <strong>{displayTitle}</strong>?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" disabled={busy} onClick={toggle}>
            Close
          </Button>
          <Button
            color="danger"
            type="button"
            disabled={busy}
            onClick={() => void runDelete()}
          >
            {busy ? "Working…" : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={action === "cancel"} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Cancel leave</ModalHeader>
        <ModalBody>
          <p className="mb-3">
            Cancel this approved application for <strong>{displayTitle}</strong>
            ? This will delete the <strong>LEAVE</strong> attendance records
            created for the leave period.
          </p>
          <Label className="form-label">Reason (optional)</Label>
          <Input
            type="textarea"
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Reason for cancellation"
          />
          <p className="text-muted small mt-2 mb-0">
            The reason will be saved and visible on the application timeline.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" disabled={busy} onClick={toggle}>
            Close
          </Button>
          <Button
            color="danger"
            type="button"
            disabled={busy}
            onClick={() => void runCancel()}
          >
            {busy ? "Working…" : "Cancel leave"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
